// src/AI/Ai.jsx
import React, { useState, useRef, useEffect } from 'react';
import { parseJwt } from '../../helpers';
import { v4 as uuidv4 } from 'uuid';
import styles from './Ai.module.scss';

const STORAGE_KEY = 'chat_conversations';
// Set to false to show the actual AI chat page
const SHOW_UNDER_CONSTRUCTION = true;

export default function Ai({ token, companydata }) {
  // 1) Cargamos todas las conversaciones del localStorage
  const [convos, setConvos] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : [];
  });

  console.log(companydata);
  const [isLoading, setIsLoading] = useState(false);

  // 2) ID de la conversación activa (puede ser null)
  const [activeId, setActiveId] = useState(convos[0]?.id ?? null);

  // 3) Mensajes de la conversación activa
  const activeConvo = convos.find(c => c.id === activeId);
  const [messages, setMessages] = useState(activeConvo?.messages ?? []);

  const [input, setInput] = useState('');
  const chatRef = useRef(null);

  // Cuando cambia activeId o convos, recargo los mensajes
  useEffect(() => {
    const convo = convos.find(c => c.id === activeId);
    setMessages(convo?.messages ?? []);
  }, [activeId, convos]);

  // Cada vez que cambia convos, persisto todo en localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(convos));
  }, [convos]);

  // Auto‐scroll al fondo de los mensajes
  useEffect(() => {
    chatRef.current?.scrollTo({
      top: chatRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [messages]);

  // Helper: crea una nueva conversación y la selecciona
  const createNewConvo = () => {
    const id = uuidv4();
    const name = `Chat ${new Date().toLocaleString()}`;
    const blank = { id, name, messages: [] };
    setConvos(prev => [blank, ...prev]);
    setActiveId(id);
    return blank;
  };
  // Helper: borra la conversación con id dado
  const deleteConvo = id => {
    const filtered = convos.filter(c => c.id !== id);
    setConvos(filtered);
    // si borramos la activa, seleccionamos la siguiente o null
    if (id === activeId) {
      setActiveId(filtered[0]?.id ?? null);
    }
  };


  const sendMessage = async () => {
    if (!input.trim()) return;

    // 1) Si no hay conversación activa, la creamos
    let convoId = activeId;
    let currentConvos = convos;
    if (!convoId) {
      const newConvo = createNewConvo();
      convoId = newConvo.id;
      currentConvos = [newConvo, ...convos];
    }

    // 2) Construimos el mensaje del usuario
    const userMessage = { role: 'user', content: input };
    const updatedMessages = [...(activeConvo?.messages ?? []), userMessage];

    // 3) Actualizamos estado local: messages y convos
    setMessages(updatedMessages);
    setConvos(cs =>
      cs.map(c =>
        c.id === convoId ? { ...c, messages: updatedMessages } : c
      )
    );

    setInput('');
    setIsLoading(true);

    // 4) Preparamos y enviamos el payload al backend
    const payload = {
      messages: updatedMessages,
      meta: {
        user_id: parseJwt(token)?.user_id,
        username: parseJwt(token)?.username,
        company_id: parseJwt(token)?.belong_id,
        company_name: companydata,
      }
    };

    try {
      const res = await fetch('http://192.168.8.184:8000/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      const { reply } = await res.json();

      // 5) Append de la respuesta de la IA
      const assistantMessage = {
        role: 'assistant',
        content: reply ?? 'Sin respuesta del servidor',
      };
      const finalMessages = [...updatedMessages, assistantMessage];
      setMessages(finalMessages);
      setConvos(cs =>
        cs.map(c =>
          c.id === convoId ? { ...c, messages: finalMessages } : c
        )
      );
      setIsLoading(false);
    } catch (err) {
      console.error(err);
      const errorMsg = { role: 'assistant', content: 'Error al conectar con el servidor.' };
      const finalMessages = [...updatedMessages, errorMsg];
      setMessages(finalMessages);
      setConvos(cs =>
        cs.map(c =>
          c.id === convoId ? { ...c, messages: finalMessages } : c
        )
      );
      setIsLoading(false);
    }
  };

  if (SHOW_UNDER_CONSTRUCTION) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', fontSize: '2rem', color: '#666' }}>
        🚧 Under Construction 🚧
      </div>
    );
  }

  return (
    <div className={styles.aiContainer}>
      {/* ========== SIDEBAR ========== */}
      <div className={styles.sidebar}>
        <button
          onClick={createNewConvo}
          className={styles.newChatButton}
        >
          + Nuevo Chat
        </button>
        <button
          onClick={() => {
            setConvos([]);
            localStorage.removeItem(STORAGE_KEY);
            setActiveId(null);
          }}
          className={styles.clearAllButton}
        >
          🗑️ Borrar todo
        </button>
        {convos.map(c => (
          <div
            key={c.id}
            onClick={() => setActiveId(c.id)}
            className={`${styles.convoItem} ${c.id === activeId ? styles.active : ''}`}
          >
            <span>
              {c.name}
            </span>
            <button
              onClick={e => {
                e.stopPropagation();
                deleteConvo(c.id);
              }}
              className={styles.deleteConvoButton}
            >
              🗑️
            </button>
          </div>
        ))}
      </div>

      {/* ======== PANEL DE CHAT ======== */}
      <div className={styles.chatPanel}>
        <div className={styles.chatBox}>
          {/* Mensajes */}
          <div
            ref={chatRef}
            className={styles.messagesContainer}
          >
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`${styles.message} ${msg.role === 'user' ? styles.user : styles.assistant}`}
              >
                {msg.content}
              </div>
            ))}
            {isLoading && (
              <div
                className={styles.loadingMessage}
              >
                Escribiendo…
              </div>
            )}
          </div>
          {/* Input fijo abajo */}
          <div
            className={styles.inputArea}
          >
            <input
              type="text"
              placeholder="Escribí tu mensaje..."
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && sendMessage()}
              className={styles.messageInput}
            />
            <button
              onClick={sendMessage}
              className={styles.sendButton}
            >
              Enviar
            </button>
          </div>
        </div>
      </div>
    </div >
  );
}
